<template>
  <div class="new-dispatch-modal">
    <!-- Filters Section -->
    <div class="dropdown-container">
      <!-- From Date -->
      <div class="dropdown-item">
        <label for="startDate">From Date <span>*</span></label>
        <input type="date" id="startDate" v-model="startDate" class="search-input" @change="handleDateChange" :max="currentDate" :disabled="isLoading" />
      </div>

      <!-- To Date -->
      <div class="dropdown-item">
        <label for="endDate">To Date <span>*</span></label>
        <input type="date" id="endDate" v-model="endDate" class="search-input" @change="handleDateChange" :max="currentDate" :disabled="isLoading" />
      </div>

      <!-- Distributor -->
      <div class="dropdown-item">
        <label for="distributor">Distributor <span>*</span></label>
        <select id="distributor" v-model="selectedDistributor" @change="onDistributorChange"
          :disabled="isDistributorLocked || isLoading">
          <option disabled value="">Select Distributor</option>
          <option v-if="!isDistributorUser" :value="ALL">All Distributors</option>
          <option v-for="d in distributorList" :key="d.profile_id" :value="String(d.profile_id)">
            {{ d.name }}
          </option>
        </select>
      </div>

      <!-- Sales Rep -->
      <div class="dropdown-item">
        <label for="salesRep">Vehicle/Yard</label>
        <select
          id="salesRep"
          v-model="selectedSalesRep"
          :disabled="isLoading"
          :class="{ 'inactive-select': selectedSalesRepIsInactive }"
        >
          <option
            v-for="option in salesRepOptions"
            :key="option.value"
            :value="option.value"
            :class="{ 'inactive-option': isInactiveOption(option) }"
          >
            {{ option.label }}
          </option>
        </select>
      </div>

      <!-- Route -->
      <div class="dropdown-item">
        <label for="salesRoute">Route</label>
        <select
          id="salesRoute"
          v-model="selectedSalesRoute"
          :disabled="isLoading"
          :class="{ 'inactive-select': selectedSalesRouteIsInactive }"
        >
          <option
            v-for="option in salesRouteOptions"
            :key="option.value"
            :value="option.value"
            :class="{ 'inactive-option': isInactiveOption(option) }"
          >
            {{ option.label }}
          </option>
        </select>
      </div>

      <!-- Search Button -->
      <div class="dropdown-item action-item search-container">
        <button @click="performSearch" class="btn button-default" :disabled="isLoading">
          <i v-if="isLoading" class="fa fa-spinner fa-spin me-1"></i>
          {{ isLoading ? "Searching..." : "Search" }}
        </button>
      </div>

      <!-- Export to Excel Button -->
      <div class="dropdown-item action-item search-container">
        <button @click="exportToExcel" class="btn button-export" :disabled="!filteredInvoices.length || isLoading">
          Export to Excel
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="pageLoading || loading" class="loading-container mt-4">
      <div class="loading-card">
        <div class="loading-spinner-wrapper">
          <i class="fa fa-spinner fa-spin loading-icon"></i>
        </div>
        <h6 class="loading-title">
          <span v-if="pageLoading">Loading page...</span>
          <span v-else>Loading Invoice Summary Report...</span>
        </h6>
        <p class="loading-subtitle">Please wait while the records are being retrieved.</p>
      </div>
    </div>

    <!-- Report Table -->
    <div v-if="!loading && !pageLoading && (searched || searchedOnce) && filteredInvoices.length > 0" id="printableReport">
      <div class="table-container">
        <table class="table table-responsive-sm">
          <thead>
            <tr>
              <th>DEALER NAME</th>
              <th>INVOICE NO</th>
			        <th>INVOICE DATE</th>
              <th v-for="itemType in itemTypes" :key="itemType">
                {{ String(itemType).toUpperCase() }}
              </th>
              <th>VALUE OF SUPPLY</th>
              <th>VAT AMOUNT</th>
              <th>TOTAL AMOUNT</th>
              <th v-for="payType in paymentTypes" :key="payType">
                {{ formatPaymentHeader(payType).toUpperCase() }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(invoice, idx) in filteredInvoices"
              :key="invoice.id || idx"
              :class="{ 'inactive-row': isDealerInactive(invoice), 'table-danger': isDealerInactive(invoice) }"
              :title="isDealerInactive(invoice) ? 'Inactive Dealer' : ''"
            >
              <td>{{ invoice.dealer_name || "N/A" }}</td>
              <td>{{ invoice.invoices_no || invoice.invoice_no || "-" }}</td>
              <td>{{ invoice.invoice_date || "-" }}</td>              

              <!-- Render item columns dynamically -->
              <td v-for="itemType in itemTypes" :key="itemType">
                {{getQuantityForItem(invoice.items, itemType) }}                
              </td>

              <td>{{ formatAmount(invoice.net_total) }}</td>
              <td>{{ formatAmount(getInvoiceVat(invoice)) }}</td>
              <td>{{ formatAmount(invoice.gross_total) }}</td>

              <td v-for="payType in paymentTypes" :key="payType">
                {{ formatAmount(getPaymentAmount(invoice, payType)) }}
              </td>
            </tr>

            <!-- Totals -->
            <tr>
              <td><strong>TOTAL</strong></td>
              <td></td>          <!-- INVOICE NO -->
              <td></td>          <!-- INVOICE DATE -->
              <td v-for="itemType in itemTypes" :key="itemType">
                <strong>{{ getTotalForItemType(itemType) }}</strong>
              </td>
              <td><strong>{{ formatAmount(totalNetAmount) }}</strong></td>
              <td><strong>{{ formatAmount(totalVatAmount) }}</strong></td>
              <td><strong>{{ formatAmount(totalGrossAmount) }}</strong></td>
              <td v-for="payType in paymentTypes" :key="payType">
                <strong>{{ formatAmount(getTotalForPaymentType(payType)) }}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modern Empty State -->
    <div v-else-if="!loading && !pageLoading && (searched || searchedOnce) && filteredInvoices.length === 0" class="no-data-container mt-4">
      <div class="no-data-card">
        <div class="no-data-icon-wrapper">
          <i class="fa fa-folder-open-o no-data-icon"></i>
        </div>
        <h5 class="no-data-title">No Data Found</h5>
        <p class="no-data-text">
          {{ searchErrorMessage || "There are no invoice records matching your selected filters." }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import "@fortawesome/fontawesome-free/css/all.min.css";
import * as XLSX from "xlsx";
import { useRouter } from "vue-router";
import {
  fetchDistributorsList,
  fetchInvoiceSummary,
  fetchRepListInvoice,
  getDistributorRouteList,
} from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  data() {
    const currentDate = new Date().toLocaleDateString("en-CA");
    return {
      token: localStorage.getItem("token_set"),
      profile_id: localStorage.getItem("profile_id") || null,
      selectedDistributor: "",
      selectedSalesRep: "all",
      selectedSalesRoute: "",
      distributorList: [],
      salesRepList: [],
      salesRouteList: [],
      startDate: currentDate,
      endDate: currentDate,
      currentDate: currentDate,
      filteredInvoices: [],
      itemTypes: [],
      paymentTypes: [],
      loading: false,
      searchErrorMessage: "",
      ALL: "__all__",
      userLabel: "",
      roleKey: "OTHER",
      myProfileId: Number(localStorage.getItem("profile_id") || 0),
      isDistributorLocked: false,
      pageLoading: false,
      loadingInit: false,
      searched: false,
      searchedOnce: false,
    };
  },

  setup() {
    const router = useRouter();
    return { router };
  },

  computed: {
    salesRepOptions() {
      return [
        { value: "all", label: "All Invoices" },
        { value: "all_vehicles", label: "All Vehicle Invoices" },
        { value: "direct", label: "All Direct Invoices" },
        ...this.salesRepList.map((r) => ({
          value: String(r.profile_id),
          label: String(r.name || "").trim(),
          active: r.is_active,
        })),
      ];
    },
    salesRouteOptions() {
      return [
        { value: "", label: "All" },
        ...this.salesRouteList.map((r) => ({
          value: String(r.route_id),
          label: r.route_name,
          active: r.is_active,
        })),
      ];
    },
    selectedSalesRepIsInactive() {
      const selected = this.salesRepOptions.find(
        (opt) => String(opt.value) === String(this.selectedSalesRep)
      );
      return this.isInactiveOption(selected);
    },
    selectedSalesRouteIsInactive() {
      const selected = this.salesRouteOptions.find(
        (opt) => String(opt.value) === String(this.selectedSalesRoute)
      );
      return this.isInactiveOption(selected);
    },
  totalGrossAmount() {
    return this.filteredInvoices.reduce((t, inv) => t + this.toNumber(inv.gross_total), 0);
  },
    totalVatAmount() {
      return this.filteredInvoices.reduce((t, inv) => t + this.getInvoiceVat(inv), 0);
    },
    totalNetAmount() {
      return this.filteredInvoices.reduce((t, inv) => t + this.toNumber(inv.net_total), 0);
    },
    totalCashAmount() {
      return this.getTotalForPaymentType("Cash");
    },
    totalChequeAmount() {
      return this.getTotalForPaymentType("Cheque");
    },
    totalBankTransferAmount() {
      return this.getTotalForPaymentType("Bank Transfer");
    },
    totalCreditAmount() {
      return this.getTotalForPaymentType("Credit");
    },
    totalOverallAmount() {
      return this.paymentTypes.reduce(
        (sum, payType) => sum + this.getTotalForPaymentType(payType),
        0
      );
    },

    isDistributorUser() { return this.roleKey === "DIS"; },

    isLoading() { return this.pageLoading || this.loadingInit || this.loading; },

  },

  created() {
    this.initRoleFromStorage();
    this.initPage();
  },

  methods: {
    toNumber(value) {
      return parseFloat(String(value || "0").replace(/,/g, "")) || 0;
    },
    formatAmount(value) {
      return this.toNumber(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    initRoleFromStorage() {
      const labelFromStorage =
        localStorage.getItem("user_label") ||
        localStorage.getItem("userLabel") ||
        localStorage.getItem("role") ||
        "";

      this.userLabel = String(labelFromStorage || "");
      const u = this.userLabel.toUpperCase().trim();

      if (u === "DISTRIBUTOR" || u.startsWith("DISTRIBUTOR") || u === "DIS" || u.startsWith("DIS_")) this.roleKey = "DIS";
      else if (u === "ASM" || u.startsWith("ASM_")) this.roleKey = "ASM";
      else if (u === "FSM" || u.startsWith("FSM_")) this.roleKey = "FSM";
      else this.roleKey = "OTHER";

      this.myProfileId = Number(localStorage.getItem("profile_id") || 0);
      this.isDistributorLocked = false;
    },

    async initPage() {
      this.pageLoading = true;
      this.loadingInit = true;
      try {
        await this.loadDistributorList();
      } finally {
        this.pageLoading = false;
        this.loadingInit = false;
      }

      // Initial report data loading
      await this.performSearch(true);
    },

    isValidISODate(dateStr) {
      if (!dateStr || typeof dateStr !== "string") return false;
      if (dateStr.includes("/")) return false;

      const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return false;

      const y = Number(m[1]);
      const mo = Number(m[2]);
      const d = Number(m[3]);

      const dt = new Date(y, mo - 1, d);
      return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
    },

    validateStartDate(showToast = true) {
      if (!this.startDate) return true;

      if (!this.isValidISODate(this.startDate)) {
        if (showToast) this.showErrorToast("Invalid From Date");
        this.startDate = "";
        return false;
      }

      if (this.startDate > this.currentDate) {
        if (showToast) this.showErrorToast("From Date cannot be in the future");
        this.startDate = "";
        return false;
      }

      return true;
    },

    validateEndDate(showToast = true) {
      if (!this.endDate) return true;

      if (!this.isValidISODate(this.endDate)) {
        if (showToast) this.showErrorToast("Invalid To Date");
        this.endDate = "";
        return false;
      }

      if (this.endDate > this.currentDate) {
        if (showToast) this.showErrorToast("To Date cannot be in the future");
        this.endDate = "";
        return false;
      }

      return true;
    },

    validateDatesForSearch() {
      if (!this.startDate) { this.showErrorToast("Please select valid From Date"); return false; }
      if (!this.endDate) { this.showErrorToast("Please select valid To Date"); return false; }

      if (!this.validateStartDate(true)) return false;
      if (!this.validateEndDate(true)) return false;

      if (this.endDate < this.startDate) {
        this.showErrorToast("Invalid date range picked.");
        return false;
      }
      return true;
    },

    isInactiveOption(option) {
      if (!option || option.active === undefined || option.active === null) return false;
      return (
        option.active === false ||
        Number(option.active) === 0 ||
        String(option.active) === "0"
      );
    },

    isDealerInactive(invoice) {
      if (!invoice) return false;
      return (
        String(invoice.is_active) === "0" ||
        invoice.is_active === 0 ||
        invoice.is_active === false
      );
    },

    getInvoiceVat(invoice) {
      if (!invoice) return 0;
      if (invoice.vat !== undefined && invoice.vat !== null) {
        return this.toNumber(invoice.vat);
      }
      if (invoice.vat_liable === "1") {
        return this.toNumber(invoice.vat_amount);
      }
      return 0;
    },

    getItemName(item) {
      return item?.variant_name || item?.item_name || "";
    },

    getItemQty(item) {
      if (!item) return 0;
      const q = item.item_qty !== undefined ? item.item_qty : item.quantity;
      return parseFloat(q) || 0;
    },

    formatPaymentHeader(name) {
      if (!name) return "";
      const upper = String(name).trim().toUpperCase();

      const exactMap = {
        "CASH": "CASH PAYMENTS",
        "CASH AMOUNT": "CASH PAYMENTS",
        "TOTAL CASH PAYMENTS": "CASH PAYMENTS",
        "CHEQUE": "CHEQUE PAYMENTS",
        "CHEQUE AMOUNT": "CHEQUE PAYMENTS",
        "TOTAL CHEQUE PAYMENTS": "CHEQUE PAYMENTS",
        "BANK TRANSFER": "BANK TRANSFER PAYMENTS",
        "BANK TRANSFER AMOUNT": "BANK TRANSFER PAYMENTS",
        "TOTAL BANK TRANSFER PAYMENTS": "BANK TRANSFER PAYMENTS",
        "ADVANCE": "ADVANCE PAYMENTS",
        "ADVANCE AMOUNT": "ADVANCE PAYMENTS",
        "TOTAL ADVANCE PAYMENTS": "ADVANCE PAYMENTS",
        "CREDIT/DEBIT CARD": "CREDIT/DEBIT CARD PAYMENTS",
        "CREDIT/DEBIT CARD AMOUNT": "CREDIT/DEBIT CARD PAYMENTS",
        "TOTAL CREDIT/DEBIT CARD PAYMENTS": "CREDIT/DEBIT CARD PAYMENTS",
        "MOBILE PAYMENT": "MOBILE PAYMENTS",
        "MOBILE PAYMENT AMOUNT": "MOBILE PAYMENTS",
        "TOTAL MOBILE PAYMENTS": "MOBILE PAYMENTS",
        "ONLINE PAYMENT": "ONLINE PAYMENTS",
        "ONLINE PAYMENT AMOUNT": "ONLINE PAYMENTS",
        "TOTAL ONLINE PAYMENTS": "ONLINE PAYMENTS",
        "CREDIT": "CREDIT AMOUNT",
        "CREDIT AMOUNT": "CREDIT AMOUNT",
        "TOTAL CREDIT AMOUNT": "CREDIT AMOUNT",
        "TOTAL CREDIT PAYMENTS": "CREDIT AMOUNT",
      };

      if (exactMap[upper]) {
        return exactMap[upper];
      }

      const stripped = upper.replace(/^TOTAL\s+/, "");
      if (stripped.startsWith("CREDIT")) {
        return "CREDIT AMOUNT";
      }
      if (stripped.endsWith(" PAYMENTS")) {
        return stripped;
      }
      const cleanName = stripped.replace(/\s*AMOUNT$/, "").replace(/\s*PAYMENT$/, "").replace(/\s*PAYMENTS$/, "");
      return `${cleanName} PAYMENTS`;
    },

    getPaymentAmount(inv, payType) {
      if (!inv) return 0;
      if (inv.payment_summary && inv.payment_summary[payType] !== undefined) {
        return this.toNumber(inv.payment_summary[payType]);
      }
      const legacyMap = {
        Cash: "cash_amount",
        Cheque: "cheque_amount",
        "Bank Transfer": "bank_transfer_amount",
        Credit: "credit_amount",
      };
      const legacyKey = legacyMap[payType];
      if (legacyKey && inv[legacyKey] !== undefined) {
        return this.toNumber(inv[legacyKey]);
      }
      return 0;
    },

    getTotalForPaymentType(payType) {
      return this.filteredInvoices.reduce((sum, inv) => {
        return sum + this.getPaymentAmount(inv, payType);
      }, 0);
    },

    getTotalAmountForInvoice(inv) {
      if (!inv) return 0;
      if (inv.payment_summary && typeof inv.payment_summary === "object") {
        return Object.values(inv.payment_summary).reduce(
          (sum, val) => sum + this.toNumber(val),
          0
        );
      }
      return (
        this.toNumber(inv.cash_amount) +
        this.toNumber(inv.cheque_amount) +
        this.toNumber(inv.bank_transfer_amount) +
        this.toNumber(inv.credit_amount)
      );
    },

    getQuantityForItem(itemsArray, itemName) {
      const item = itemsArray?.find((i) => this.getItemName(i) === itemName);
      if (!item) return 0;
      const qty = item.item_qty !== undefined ? item.item_qty : item.quantity;
      return qty !== null && qty !== undefined && qty !== "" ? qty : 0;
    },

    getTotalForItemType(itemName) {
      const total = this.filteredInvoices.reduce((sum, inv) => {
        const item = inv.items?.find((i) => this.getItemName(i) === itemName);
        return sum + this.getItemQty(item);
      }, 0);
      return total;
    },

    handleDateChange() {
      this.validateStartDate(false);
      this.validateEndDate(false);

      if (this.startDate && this.endDate && this.endDate < this.startDate) {
        this.showErrorToast("Invalid date range picked.");
        this.endDate = "";
      }
    },

    showErrorToast(msg) {
      const toast = useToast();
      toast.error(msg, { position: "top-right", timeout: 3000 });
    },

    showInfoToast(msg) {
      const toast = useToast();
      toast.info(msg, { position: "top-right", timeout: 3000 });
    },

    showSuccessToast(msg) {
      const toast = useToast();
      toast.success(msg, { position: "top-right", timeout: 3000 });
    },

    async loadDistributorList() {
      try {
        const res = await fetchDistributorsList(this.profile_id, this.token);
        let list = res?.data?.data || [];

        // reset
        this.isDistributorLocked = false;

        // ✅ Distributor login: auto pick own distributor + lock
        if (this.isDistributorUser && this.myProfileId) {
          const mine = list.find(d => Number(d.profile_id) === Number(this.myProfileId));

          if (mine) {
            // lock to own distributor
            list = [mine];
            this.selectedDistributor = String(mine.profile_id);
            this.isDistributorLocked = true;

            // ✅ load reps for this distributor (because programmatic set won't trigger @change)
            this.selectedSalesRep = "all";
            this.salesRepList = [];
            await this.loadSalesRepList();


            this.selectedSalesRoute = "";
            this.salesRouteList = [];
            await this.loadRouteList(this.selectedDistributor);

          } else {
            // if not found, clear everything
            this.selectedDistributor = "";
            this.selectedSalesRep = "all";
            this.salesRepList = [];
            this.isDistributorLocked = false;
            this.selectedSalesRoute = "";
            this.salesRouteList = [];

          }
        } else {
          // other users: default to "ALL" so all distributor data loads initially
          this.selectedDistributor = this.ALL;
          this.selectedSalesRep = "all";
          this.salesRepList = [];
          await this.loadRouteList(null);
        }

        this.distributorList = list;
      } catch (err) {
        console.error(err);
        this.distributorList = [];
        this.selectedDistributor = "";
        this.selectedSalesRep = "all";
        this.salesRepList = [];
        this.isDistributorLocked = false;
      }
    },

    async onDistributorChange() {
      // reset rep + route filters
      this.selectedSalesRep = "all";
      this.salesRepList = [];

      this.selectedSalesRoute = "";
      this.salesRouteList = [];

      if (!this.selectedDistributor) return;

      // ALL selected -> load "general/all" routes (fallback by profile_id)
      if (this.selectedDistributor === this.ALL) {
        await this.loadRouteList(null);
        return;
      }

      // load reps + routes for selected distributor
      await Promise.all([
        this.loadSalesRepList(),
        this.loadRouteList(this.selectedDistributor),
      ]);
    },

    async loadSalesRepList() {
      this.salesRepList = [];
      this.selectedSalesRep = "all";

      if (!this.selectedDistributor) return;
      if (this.selectedDistributor === this.ALL) return;

      try {
        // ✅ IMPORTANT: keep same as old working code
        const res = await fetchRepListInvoice(
          this.selectedDistributor,
          this.selectedDistributor,
          this.token,
          true,
        );

        this.salesRepList = res?.data?.data || [];
      } catch (err) {
        console.error("Error loading sales reps:", err?.response?.data || err);
        this.salesRepList = [];
      }
    },

    async loadRouteList(distributorId = null) {
      try {
        // ✅ if distributor selected -> use that id as profileId for API
        // ✅ if ALL / empty -> fallback to logged-in profile_id (keeps old behavior)
        const idToUse =
          distributorId && distributorId !== this.ALL
            ? distributorId
            : this.profile_id;

        const res = await getDistributorRouteList(idToUse, this.token, false);
        // normalise API fields (id / is_active) to what the dropdown + search use
        this.salesRouteList = (res?.data?.data || []).map((r) => ({
          ...r,
          route_id: r.id,
          is_active: String(r.is_active) === "1",
        }));
      } catch (err) {
        console.error("Error loading routes:", err?.response?.data || err);
        this.salesRouteList = [];
      }
    },

    async performSearch(isInitial = false) {
      const isInitialLoad = isInitial === true;

      // reset results first (ok)
      this.filteredInvoices = [];
      this.itemTypes = [];
      this.paymentTypes = [];
      this.searchErrorMessage = "";

      // ✅ validations BEFORE enabling loader + searchedOnce
      if (!this.validateDatesForSearch()) return;

      if (!this.selectedDistributor && this.isDistributorUser) {
        if (!isInitialLoad) this.showErrorToast("Please select a Distributor.");
        return;
      }

      if (this.isDistributorUser && this.selectedDistributor === this.ALL) {
        if (!isInitialLoad) this.showErrorToast("Please select a Distributor.");
        return;
      }

      // If non-distributor user has no distributor selected, default to ALL
      if (!this.isDistributorUser && !this.selectedDistributor) {
        this.selectedDistributor = this.ALL;
      }

      // ✅ now set flags
      this.searched = true;
      this.searchedOnce = true;
      this.loading = true;

      try {
        const distributorId =
          this.selectedDistributor === this.ALL || !this.selectedDistributor
            ? ""
            : String(this.selectedDistributor);

        // Sales Rep dropdown -> invoice_source / vehicle_id
        const sel = String(this.selectedSalesRep || "all");
        const isSource = ["all", "all_vehicles", "direct"].includes(sel);
        const invoiceSource = isSource ? sel : "specific_vehicle";

        // all_vehicles / direct need a distributor_profile_id
        if (["all_vehicles", "direct"].includes(invoiceSource) && !distributorId) {
          this.loading = false;
          if (!isInitialLoad) this.showErrorToast("Please select a Distributor.");
          return;
        }

        const searchData = {
          start_date: this.startDate,
          end_date: this.endDate,
          invoice_source: invoiceSource,
          route_id: this.selectedSalesRoute ? String(this.selectedSalesRoute) : "",
        };
        if (distributorId) searchData.distributor_profile_id = distributorId;
        if (invoiceSource === "specific_vehicle") searchData.vehicle_id = sel;

        const res = await fetchInvoiceSummary(searchData, this.token);

        if (res?.data && res.data.status === false) {
          this.searchErrorMessage = res.data.message || "Failed to fetch data";
          this.showErrorToast(this.searchErrorMessage);
          this.filteredInvoices = [];
          return;
        }

        const invoiceList = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
          ? res.data
          : [];
        this.filteredInvoices = invoiceList;

        // Collect all unique item names and sort by variant_id if available
        const itemMap = new Map();
        const paymentSet = new Set();
        const standardPaymentOrder = [
          "Cash",
          "Cheque",
          "Bank Transfer",
          "Advance",
          "Credit/Debit Card",
          "Mobile Payment",
          "Online Payment",
          "Credit",
        ];

        this.filteredInvoices.forEach((inv) => {
          inv.items?.forEach((i) => {
            const name = this.getItemName(i);
            if (name && !itemMap.has(name)) {
              itemMap.set(name, Number(i.variant_id || i.item_id) || 0);
            }
          });

          if (inv.payment_summary && typeof inv.payment_summary === "object") {
            Object.keys(inv.payment_summary).forEach((k) => paymentSet.add(k));
          }
        });

        this.itemTypes = Array.from(itemMap.keys()).sort((a, b) => {
          const idA = itemMap.get(a);
          const idB = itemMap.get(b);
          if (idA && idB) return idA - idB;
          return 0;
        });

        if (paymentSet.size > 0) {
          this.paymentTypes = [
            ...standardPaymentOrder.filter((k) => paymentSet.has(k)),
            ...Array.from(paymentSet).filter((k) => !standardPaymentOrder.includes(k)),
          ];
        } else {
          this.paymentTypes = ["Cash", "Cheque", "Bank Transfer", "Credit"];
        }

        if (this.filteredInvoices.length === 0) {
          const apiMsg = res?.data?.message || "";
          this.searchErrorMessage =
            apiMsg && !apiMsg.toLowerCase().includes("success")
              ? apiMsg
              : "No invoice records match your selected criteria. Please adjust your date range or filters and search again.";
          if (!isInitialLoad) {
            this.showInfoToast("No data found for the selected criteria");
          }
        } else {
          this.searchErrorMessage = "";
          this.showSuccessToast(res?.data?.message || `Report loaded successfully with ${this.filteredInvoices.length} invoices`);
        }
      } catch (err) {
        console.error("Error fetching invoice summary:", err?.response?.data || err);
        const msg = err?.response?.data?.message || "Error loading report. Please try again.";
        this.searchErrorMessage = msg;
        this.showErrorToast(msg);
      } finally {
        this.loading = false;
      }
    },

    exportToExcel() {
      if (!this.filteredInvoices.length) {
        this.showErrorToast("No data available to export.");
        return;
      }

      const sheetData = this.filteredInvoices.map((inv) => {
        const row = {
          "DEALER NAME": inv.dealer_name || "N/A",
          "INVOICE NO": inv.invoices_no || inv.invoice_no || "-",
          "INVOICE DATE": inv.invoice_date || "-",
        };
        this.itemTypes.forEach((item) => {
          row[String(item).toUpperCase()] = this.getQuantityForItem(inv.items, item);
        });
        row["VALUE OF SUPPLY"] = this.formatAmount(inv.net_total);
        row["VAT AMOUNT"] = this.formatAmount(this.getInvoiceVat(inv));
        row["TOTAL AMOUNT"] = this.formatAmount(inv.gross_total);
        this.paymentTypes.forEach((payType) => {
          row[this.formatPaymentHeader(payType).toUpperCase()] = this.formatAmount(
            this.getPaymentAmount(inv, payType)
          );
        });
        return row;
      });

      // Add totals row in Excel
      const totalRow = {
        "DEALER NAME": "TOTAL",
        "INVOICE NO": "",
        "INVOICE DATE": "",
      };
      this.itemTypes.forEach((item) => {
        totalRow[String(item).toUpperCase()] = this.getTotalForItemType(item);
      });
      totalRow["VALUE OF SUPPLY"] = this.formatAmount(this.totalNetAmount);
      totalRow["VAT AMOUNT"] = this.formatAmount(this.totalVatAmount);
      totalRow["TOTAL AMOUNT"] = this.formatAmount(this.totalGrossAmount);
      this.paymentTypes.forEach((payType) => {
        totalRow[this.formatPaymentHeader(payType).toUpperCase()] = this.formatAmount(
          this.getTotalForPaymentType(payType)
        );
      });
      sheetData.push(totalRow);

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Invoice Summary");
      const fileName = `Invoice_Summary_${this.startDate}_to_${this.endDate}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      this.showSuccessToast("Excel downloaded successfully!");
    },
  },
};
</script>

<style scoped>
.new-dispatch-modal {
  padding: 20px;
  background-color: #fff;
  border-radius: 10px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  position: relative;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

h2 {
  color: #1b2a50;
  font-size: 24px;
}



.btn {
  height: 41px;
  padding: 0 18px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

.btn.button-default {
  background-color: #007bff;
  color: #fff;
}

.btn.button-default:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn.button-export {
  background-color: #28a745;
  color: #fff;
}

.btn.button-export:hover:not(:disabled) {
  background-color: #218838;
}

.btn:disabled {
  background-color: #6c757d !important;
  color: #fff !important;
  cursor: not-allowed;
  opacity: 0.7;
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  margin-bottom: 10px;
}

input[type="checkbox"] {
  margin-right: 10px;
}

.text-center {
  text-align: center;
}

.table-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-top: 20px;

  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #ddd;

  position: relative;
  z-index: 0;
}

.table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
}

.table th,
.table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
  overflow: visible;
  text-overflow: clip;
  min-width: 100px;
}

.table th {
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 1;
  font-weight: bold;
  text-transform: uppercase;
}

.table td:nth-child(n+3):not(:last-child) {
  text-align: left;
}

.table td:last-child,
.table td:nth-last-child(2),
.table td:nth-last-child(3) {
  text-align: left;
}

.table tr:last-child {
  font-weight: bold;
  background-color: #f1f1f1;
}

.table tr.inactive-row,
.table tr.inactive-row td {
  background-color: #ffebee !important;
}

.table tr.inactive-row:hover,
.table tr.inactive-row:hover td {
  background-color: #ffdcdb !important;
}

.table th,
.table td {
  padding-left: 20px;
  padding-right: 20px;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

select:focus,
select:active,
input[type="date"]:focus,
input[type="date"]:active {
  border-color: black;
  outline: none;
}

select,
input[type="date"],
.items-per-page {
  transition: border-color 0.3s ease;
}




.table th {
  position: sticky;
  top: 0;
  z-index: 10;
}

/* =========================================================
   RESPONSIVE REPORT FILTERS
========================================================= */

/* =========================================================
   RESPONSIVE REPORT FILTERS & ACTIONS
========================================================= */

.dropdown-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  width: 100%;
  margin-bottom: 20px;
  align-items: end;
  box-sizing: border-box;
}

.dropdown-item {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.dropdown-item label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 13px;
  color: #1b2a50;
  white-space: nowrap;
}

.dropdown-item label span {
  color: #dc3545;
}

.dropdown-item select,
.dropdown-item input[type="date"],
.dropdown-item .search-input {
  width: 100%;
  height: 40px;
  padding: 8px 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  box-sizing: border-box;
  background-color: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.dropdown-item select:focus,
.dropdown-item input[type="date"]:focus,
.dropdown-item .search-input:focus {
  border-color: #007bff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
}

.dropdown-item select:disabled,
.dropdown-item input[type="date"]:disabled,
.dropdown-item .search-input:disabled {
  background-color: #e9ecef;
  color: #7a8494;
  cursor: not-allowed;
}

/* Default dropdown options (prevent inheriting inactive-select colors) */
.dropdown-item select option {
  background-color: #fff;
  color: #333;
}

/* Inactive vehicle/route: dropdown options */
.dropdown-item select option.inactive-option {
  background-color: #fde8e8;
  color: #b42318;
}

/* Select box when an inactive vehicle/route is the selected value */
.dropdown-item select.inactive-select {
  background-color: #fde8e8;
  color: #b42318 !important;
  border-color: #f1aeb5;
}

.dropdown-item.action-item {
  display: flex;
  align-items: flex-end;
}

.dropdown-item.action-item .btn {
  width: 100%;
  height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

/* Medium devices (tablets 576px - 767px): 2 columns */
@media (min-width: 576px) and (max-width: 767px) {
  .dropdown-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
}

/* Tablets / Laptops (768px - 1099px): 3 to 4 columns */
@media (min-width: 768px) and (max-width: 1099px) {
  .dropdown-container {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }
}

/* Desktops and wide screens (>= 1100px): ALL filters & buttons in a SINGLE ROW */
@media (min-width: 1100px) {
  .dropdown-container {
    grid-template-columns: minmax(130px, 1fr) minmax(130px, 1fr) minmax(160px, 1.4fr) minmax(120px, 1.1fr) minmax(120px, 1.1fr) auto auto;
    gap: 10px;
    align-items: end;
  }

  .dropdown-item {
    width: auto;
  }

  .dropdown-item.action-item .btn {
    width: auto;
  }
}

@media (max-width: 575px) {
  .new-dispatch-modal {
    padding: 14px;
  }
}

/* =========================================================
   LOADING STATE
========================================================= */
.loading-container {
  width: 100%;
}

.loading-card {
  text-align: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 42px 24px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.loading-spinner-wrapper {
  margin-bottom: 12px;
}

.loading-icon {
  font-size: 32px;
  color: #007bff;
}

.loading-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
}

.loading-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 0;
}

/* =========================================================
   EMPTY STATE (NO DATA)
========================================================= */
.no-data-container {
  width: 100%;
  margin-top: 24px;
}

.no-data-card {
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 48px 32px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease-in-out;
}

.no-data-card:hover {
  border-color: #94a3b8;
  background: #f1f5f9;
}

.no-data-icon-wrapper {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px auto;
  color: #64748b;
  font-size: 32px;
  transition: transform 0.2s ease;
}

.no-data-card:hover .no-data-icon-wrapper {
  transform: scale(1.05);
  background: #dbeafe;
  color: #2563eb;
}

.no-data-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.no-data-text {
  font-size: 14px;
  color: #64748b;
  max-width: 640px;
  margin: 0 auto 16px auto;
  line-height: 1.5;
}
</style>